import { StorageSOPClassUID } from '../dicom/modality';

// Helper types
enum TransferSyntaxUID {
    ImplicitVRLittleEndian = '1.2.840.10008.1.2',
    ExplicitVRLittleEndian = '1.2.840.10008.1.2.1',
    ExplicitVRBigEndian = '1.2.840.10008.1.2.2',
}
type DetectResult = {
    isLittleEndian: boolean;
    isExplicitVR: boolean;
    transferSyntaxUID: TransferSyntaxUID;
    confidence: number; // higher is better
    offsetUsed: number; // where detection started
};

// Hard-coded implementation identifiers for the meta information header
const workspaceImplementationClassUID = '2.25.19722164020413952199371',
    workspaceImplementationVersionName = 'MVWS';

/**
 * Helper class for handling DICOM headers and transfer syntax detection. See README.md for details.
 */
export abstract class HeaderHelper {
    /**
     * Checks if an ArrayBuffer is a valid DICOM file and adds File Meta Information if needed.
     * @param arrayBuffer The input ArrayBuffer containing potential DICOM data
     * @returns A valid DICOM ArrayBuffer (either original or newly created)
     */
    public static ensureValidDicom(arrayBuffer: ArrayBuffer): ArrayBuffer {
        // Check if the file already has a valid DICOM header
        if (HeaderHelper.isValidDicom(arrayBuffer)) {
            return arrayBuffer;
        }

        // Create a new DICOM array with proper File Meta Information
        return HeaderHelper.fixMissingMetaheader(arrayBuffer);
    }

    /**
     * Checks if an ArrayBuffer starts with a valid DICOM header
     */
    private static isValidDicom(arrayBuffer: ArrayBuffer): boolean {
        if (arrayBuffer.byteLength < 132) {
            return false;
        }

        const headerView = new DataView(arrayBuffer, 128, 8);
        return (
            headerView.getUint8(0) === 0x44 && // 'D'
            headerView.getUint8(1) === 0x49 && // 'I'
            headerView.getUint8(2) === 0x43 && // 'C'
            headerView.getUint8(3) === 0x4d && // 'M'
            headerView.getUint8(4) === 0x02 && // File Meta Element tag group (0002,0000)
            headerView.getUint8(5) === 0x00 &&
            headerView.getUint8(6) === 0x00 &&
            headerView.getUint8(7) === 0x00
        );
    }

    private static fixMissingMetaheader(arrayBuffer: ArrayBuffer): ArrayBuffer {
        const originalFile = new Uint8Array(arrayBuffer);

        const headerView = new DataView(arrayBuffer, 128, 16);
        const hasDicmPrefix =
            headerView.getUint8(0) === 0x44 && // 'D'
            headerView.getUint8(1) === 0x49 && // 'I'
            headerView.getUint8(2) === 0x43 && // 'C'
            headerView.getUint8(3) === 0x4d; // 'M'

        const transferSyntax = HeaderHelper.detectTransferSyntax(arrayBuffer);
        console.debug(
            `NOTICE: DICOM file does not have a valid DICOM Meta Information Header. Inserting a preset header. Detected transfer syntax: ${transferSyntax.transferSyntaxUID} (Explicit VR: ${transferSyntax.isExplicitVR}, Little Endian: ${transferSyntax.isLittleEndian})`
        );

        // Required header elements:
        // * Empty preamble
        // * "DICM" prefix
        // * (0002,0000) *File Meta Information Group Length*, UL, 4
        // * (0002,0001) *File Meta Information Version*, OB, 2
        // * (0002,0002) *Media Storage SOP Class UID*, UI, N
        // * (0002,0003) *Media Storage SOP Instance UID*, UI, N
        // * (0002,0010) *Transfer Syntax UID*, UI, N
        // * (0002,0012) *Implementation Class UID*, UI, N

        // Map detection → TransferSyntaxUID
        const tsUID = transferSyntax.transferSyntaxUID; // "1.2.840.10008.1.2" | "1.2.840.10008.1.2.1" | "1.2.840.10008.1.2.2"

        // Build a metaheader that matches the detected syntax:
        const metaheader = HeaderHelper.buildMetaheader({
            mediaStorageSOPClassUID: StorageSOPClassUID.CTImageStorage, // CT Image Storage
            mediaStorageSOPInstanceUID: '2.25.101485446345160521080014060', // SOP Instance UID, note that this is a dummy value and does not match the actual dataset!
            transferSyntaxUID: tsUID, // from detection
            implementationClassUID: '2.25.19722164020413952199371',
            implementationVersionName: 'MVWS',
        });

        const originalData = hasDicmPrefix
            ? originalFile.slice(132)
            : originalFile;

        const combined = new Uint8Array([...metaheader, ...originalData]);

        return combined.buffer;
    }

    private static asciiBytes(s: string): Uint8Array {
        const out = new Uint8Array(s.length);
        for (let i = 0; i < s.length; i++) {
            out[i] = s.charCodeAt(i) & 0xff;
        }
        return out;
    }

    private static le16(n: number) {
        return new Uint8Array([n & 0xff, (n >>> 8) & 0xff]);
    }

    private static le32(n: number) {
        return new Uint8Array([
            n & 0xff,
            (n >>> 8) & 0xff,
            (n >>> 16) & 0xff,
            (n >>> 24) & 0xff,
        ]);
    }
    private static padEvenUI(s: string) {
        const b = HeaderHelper.asciiBytes(s);
        if (b.length % 2 === 1) {
            const out = new Uint8Array(b.length + 1);
            out.set(b, 0);
            // UI pads with 0x00
            out[out.length - 1] = 0x00;
            return out;
        }
        return b;
    }
    private static padEvenSH(s: string) {
        const b = HeaderHelper.asciiBytes(s);
        if (b.length % 2 === 1) {
            const out = new Uint8Array(b.length + 1);
            out.set(b, 0);
            // SH pads with 0x20 (space)
            out[out.length - 1] = 0x20;
            return out;
        }
        return b;
    }
    private static concat(parts: Uint8Array[]): Uint8Array {
        const total = parts.reduce((n, a) => n + a.length, 0);
        const out = new Uint8Array(total);
        let off = 0;
        for (const p of parts) {
            out.set(p, off);
            off += p.length;
        }
        return out;
    }

    /** Build a single Explicit-VR element header+value (group 0002 only) */
    private static elem(
        tagGroup: number,
        tagElem: number,
        vr: string,
        value: Uint8Array
    ): Uint8Array {
        const tag = HeaderHelper.concat([
            HeaderHelper.le16(tagGroup),
            HeaderHelper.le16(tagElem),
        ]);
        const vrBytes = HeaderHelper.asciiBytes(vr);
        const longVR =
            vr === 'OB' ||
            vr === 'OW' ||
            vr === 'OF' ||
            vr === 'SQ' ||
            vr === 'UT' ||
            vr === 'UN';
        if (longVR) {
            return HeaderHelper.concat([
                tag,
                vrBytes,
                new Uint8Array([0, 0]),
                HeaderHelper.le32(value.length),
                value,
            ]);
        } else {
            if (value.length > 0xffff) {
                throw new Error(`Value too long for short VR ${vr}`);
            }
            return HeaderHelper.concat([
                tag,
                vrBytes,
                HeaderHelper.le16(value.length),
                value,
            ]);
        }
    }

    /** Build a complete PS3.10 metaheader (starts with 'DICM'; add preamble if requested) */
    private static buildMetaheader(opts: {
        mediaStorageSOPClassUID: string; // e.g., "1.2.840.10008.5.1.4.1.1.2" (CT)
        mediaStorageSOPInstanceUID: string;
        // From detection:
        transferSyntaxUID: DetectResult['transferSyntaxUID'];
        implementationClassUID?: string; // default below
        implementationVersionName?: string; // default below
    }): Uint8Array {
        const {
            mediaStorageSOPClassUID,
            mediaStorageSOPInstanceUID,
            transferSyntaxUID,
            implementationClassUID = workspaceImplementationClassUID,
            implementationVersionName = workspaceImplementationVersionName,
        } = opts;

        // (0002,0001) OB FileMetaInformationVersion = 00 01
        const el00020001 = HeaderHelper.elem(
            0x0002,
            0x0001,
            'OB',
            new Uint8Array([0x00, 0x01])
        );
        // (0002,0002) UI MediaStorageSOPClassUID
        const el00020002 = HeaderHelper.elem(
            0x0002,
            0x0002,
            'UI',
            HeaderHelper.padEvenUI(mediaStorageSOPClassUID)
        );
        // (0002,0003) UI MediaStorageSOPInstanceUID
        const el00020003 = HeaderHelper.elem(
            0x0002,
            0x0003,
            'UI',
            HeaderHelper.padEvenUI(mediaStorageSOPInstanceUID)
        );
        // (0002,0010) UI TransferSyntaxUID (from detection)
        const el00020010 = HeaderHelper.elem(
            0x0002,
            0x0010,
            'UI',
            HeaderHelper.padEvenUI(transferSyntaxUID)
        );
        // (0002,0012) UI ImplementationClassUID
        const el00020012 = HeaderHelper.elem(
            0x0002,
            0x0012,
            'UI',
            HeaderHelper.padEvenUI(implementationClassUID)
        );
        // (0002,0013) SH ImplementationVersionName
        const el00020013 = HeaderHelper.elem(
            0x0002,
            0x0013,
            'SH',
            HeaderHelper.padEvenSH(implementationVersionName)
        );

        // Concatenate all 0002 elements except the Group Length (we compute it now)
        const metaTail = HeaderHelper.concat([
            el00020001,
            el00020002,
            el00020003,
            el00020010,
            el00020012,
            el00020013,
        ]);
        const groupLengthValue = HeaderHelper.le32(metaTail.length);
        // (0002,0000) UL FileMetaInformationGroupLength
        const el00020000 = HeaderHelper.elem(
            0x0002,
            0x0000,
            'UL',
            groupLengthValue
        );

        const magic = HeaderHelper.asciiBytes('DICM');
        const meta = HeaderHelper.concat([el00020000, metaTail]);

        const preamble = new Uint8Array(128); // all zeros
        return HeaderHelper.concat([preamble, magic, meta]);
    }

    private static VR_SET = new Set([
        'AE',
        'AS',
        'AT',
        'CS',
        'DA',
        'DS',
        'DT',
        'FL',
        'FD',
        'IS',
        'LO',
        'LT',
        'OB',
        'OD',
        'OF',
        'OL',
        'OW',
        'PN',
        'SH',
        'SL',
        'SQ',
        'SS',
        'ST',
        'SV',
        'TM',
        'UC',
        'UI',
        'UL',
        'UN',
        'UR',
        'US',
        'UT',
        'UV',
    ]);

    private static LONG_VR = new Set(['OB', 'OW', 'OF', 'SQ', 'UT', 'UN']); // explicit VR with 00 00 + 4-byte length

    // Minimal tag→expected VRs for early/common elements.
    // (We only need a handful to make sniffing robust.)
    private static TAG_VR_HINT: Record<string, string[]> = {
        '00080005': ['CS'], // SpecificCharacterSet
        '00080008': ['CS'], // ImageType
        '00080016': ['UI'], // SOPClassUID
        '00080018': ['UI'], // SOPInstanceUID
        '00100010': ['PN'], // PatientName
        '00280002': ['US'], // SamplesPerPixel
        '00280010': ['US'], // Rows
        '00280011': ['US'], // Columns
        '7FE00010': ['OB', 'OW', 'OF', 'UN'], // PixelData
        FFFEE000: [], // Item (for sequences)
        'FFFE E000': [], // (spaced form)
    };

    private static toTagKey(group: number, element: number): string {
        return ((group << 16) | element)
            .toString(16)
            .padStart(8, '0')
            .toUpperCase();
    }

    private static isAsciiUpperLetter(n: number) {
        return n >= 0x41 && n <= 0x5a;
    }
    private static isValidVRPair(b4: number, b5: number) {
        return (
            HeaderHelper.isAsciiUpperLetter(b4) &&
            HeaderHelper.isAsciiUpperLetter(b5) &&
            HeaderHelper.VR_SET.has(String.fromCharCode(b4, b5))
        );
    }

    private static looksLikeUI(bytes: Uint8Array): boolean {
        // digits/dots (and optional trailing 0x00 pad)
        const n = bytes.length;
        const last = n > 0 ? bytes[n - 1] : 0;
        const lim = last === 0x00 ? n - 1 : n;
        if (lim === 0) {
            return false;
        }
        for (let i = 0; i < lim; i++) {
            const c = bytes[i];
            const isDigit = c >= 0x30 && c <= 0x39;
            const isDot = c === 0x2e;
            if (!isDigit && !isDot) {
                return false;
            }
        }
        return true;
    }
    private static looksLikeCS(bytes: Uint8Array): boolean {
        // uppercase letters, digits, space, underscore, and a few safe symbols (permissive)
        for (const c of bytes) {
            const isUpper = c >= 0x41 && c <= 0x5a;
            const isDigit = c >= 0x30 && c <= 0x39;
            if (
                !(
                    isUpper ||
                    isDigit ||
                    c === 0x5f ||
                    c === 0x20 ||
                    c === 0x2d ||
                    c === 0x5c ||
                    c === 0x2e
                )
            ) {
                return false;
            }
        }
        return true;
    }

    private static readTag(view: DataView, offset: number, little: boolean) {
        const group = view.getUint16(offset + 0, little);
        const elem = view.getUint16(offset + 2, little);
        return { group, elem };
    }

    private static sniffAt(view: DataView, offset: number): DetectResult[] {
        const out: DetectResult[] = [];
        const end = view.byteLength;

        for (const little of [true, false]) {
            if (offset + 8 > end) {
                continue;
            }
            const { group, elem } = HeaderHelper.readTag(view, offset, little);
            const tagKey = HeaderHelper.toTagKey(group, elem);

            // Early plausibility: groups are often 0x0008/0x0010/0x0020/0x0028. Private odd groups possible, allow them.
            // Bail if the tag is ridiculously high but allow 0xFFFE (items) & 0x7FE0 (pixel data)
            const plausibleTag =
                group === 0xfffe ||
                group === 0x7fe0 ||
                group <= 0x0100 ||
                (group >= 0x0010 && group <= 0x0030) ||
                group === 0x0028 ||
                group === 0x0008;

            // --- Try EXPLICIT VR ---
            let scoreExplicit = 0;
            let explicitOK = false;
            let explicitVR = '';
            let explicitTotalLen = 0;
            if (offset + 8 <= end) {
                const b4 = view.getUint8(offset + 4);
                const b5 = view.getUint8(offset + 5);
                if (HeaderHelper.isValidVRPair(b4, b5)) {
                    explicitVR = String.fromCharCode(b4, b5);
                    if (HeaderHelper.LONG_VR.has(explicitVR)) {
                        if (offset + 12 <= end) {
                            const reserved0 = view.getUint8(offset + 6);
                            const reserved1 = view.getUint8(offset + 7);
                            const vlen = view.getUint32(offset + 8, little);
                            const valueEnd = offset + 12 + vlen;
                            if (
                                reserved0 === 0 &&
                                reserved1 === 0 &&
                                valueEnd <= end
                            ) {
                                explicitOK = true;
                                explicitTotalLen = 12 + vlen;
                                scoreExplicit += 4; // valid VR + long form correct
                            }
                        }
                    } else {
                        const vlen = view.getUint16(offset + 6, little);
                        const valueEnd = offset + 8 + vlen;
                        if (valueEnd <= end) {
                            explicitOK = true;
                            explicitTotalLen = 8 + vlen;
                            scoreExplicit += 4; // valid VR + short form ok
                        }
                    }
                    // If we have a hint for this tag, reward VR match
                    const hint = HeaderHelper.TAG_VR_HINT[tagKey];
                    if (
                        explicitOK &&
                        hint &&
                        hint.length &&
                        hint.includes(explicitVR)
                    ) {
                        scoreExplicit += 2;
                    }

                    // Light sanity on value for common VRs
                    if (explicitOK) {
                        const valOff = HeaderHelper.LONG_VR.has(explicitVR)
                            ? offset + 12
                            : offset + 8;
                        const vlen = explicitTotalLen - (valOff - offset);
                        const bytes = new Uint8Array(
                            view.buffer,
                            view.byteOffset + valOff,
                            vlen
                        );
                        if (
                            explicitVR === 'UI' &&
                            HeaderHelper.looksLikeUI(bytes)
                        ) {
                            scoreExplicit += 1;
                        }
                        if (
                            explicitVR === 'CS' &&
                            HeaderHelper.looksLikeCS(bytes)
                        ) {
                            scoreExplicit += 1;
                        }
                    }
                }
            }
            if (explicitOK) {
                out.push({
                    isLittleEndian: little,
                    isExplicitVR: true,
                    transferSyntaxUID: little
                        ? TransferSyntaxUID.ExplicitVRLittleEndian
                        : TransferSyntaxUID.ExplicitVRBigEndian,
                    confidence: scoreExplicit + (plausibleTag ? 1 : 0),
                    offsetUsed: offset,
                });
            }

            // --- Try IMPLICIT VR ---
            let scoreImplicit = 0;
            if (offset + 8 <= end) {
                const vlen = view.getUint32(offset + 4, little);
                const valueEnd = offset + 8 + vlen;
                if (valueEnd <= end) {
                    scoreImplicit += 3; // fits
                    // If we have a VR hint, very rough plausibility: small lengths for early elements
                    const hint = HeaderHelper.TAG_VR_HINT[tagKey];
                    if (hint && vlen <= 64) {
                        scoreImplicit += 1;
                    }
                    // quick sanity for typical first tags (e.g., SpecificCharacterSet/UI)
                    if (hint?.includes('UI') && vlen > 0 && vlen <= 64) {
                        const bytes = new Uint8Array(
                            view.buffer,
                            view.byteOffset + offset + 8,
                            vlen
                        );
                        if (HeaderHelper.looksLikeUI(bytes)) {
                            scoreImplicit += 1;
                        }
                    }
                    if (hint?.includes('CS') && vlen > 0 && vlen <= 64) {
                        const bytes = new Uint8Array(
                            view.buffer,
                            view.byteOffset + offset + 8,
                            vlen
                        );
                        if (HeaderHelper.looksLikeCS(bytes)) {
                            scoreImplicit += 1;
                        }
                    }
                    out.push({
                        isLittleEndian: little,
                        isExplicitVR: false,
                        transferSyntaxUID: little
                            ? TransferSyntaxUID.ImplicitVRLittleEndian
                            : TransferSyntaxUID.ExplicitVRBigEndian,
                        confidence: scoreImplicit + (plausibleTag ? 1 : 0),
                        offsetUsed: offset,
                    });
                }
            }
        }

        return out;
    }

    private static detectTransferSyntax(
        buffer: ArrayBuffer,
        options?: { scanBytes?: number }
    ): DetectResult {
        const view = new DataView(buffer);
        const scanBytes = options?.scanBytes ?? 1024;

        // For headerless streams, sniff from the start and a small window.

        let best: DetectResult | null = null;
        // scan on 2-byte boundaries to keep tag alignment
        const maxOff = Math.min(view.byteLength, scanBytes);
        for (let off = 0; off + 8 <= maxOff; off += 2) {
            const candidates = HeaderHelper.sniffAt(view, off);
            for (const c of candidates) {
                if (!best || c.confidence > best.confidence) {
                    best = c;
                }
                // Early exit if we're very confident
                if (best && best.confidence >= 7) {
                    return best;
                }
            }
        }

        // Fallback: if nothing plausible, assume Implicit VR Little Endian (historical baseline)
        if (!best) {
            best = {
                isLittleEndian: true,
                isExplicitVR: false,
                transferSyntaxUID: TransferSyntaxUID.ImplicitVRLittleEndian,
                confidence: 0,
                offsetUsed: 0,
            };
        }

        // Clean up "Implicit VR Big Endian" case (not defined in DICOM): downgrade to Explicit VR BE if that won by mistake.
        if (!best.isExplicitVR && !best.isLittleEndian) {
            // Very rare path: choose BE Explicit as more plausible if we had any explicit BE candidate near it.
            const alt = HeaderHelper.sniffAt(view, best.offsetUsed).find(
                (c) => c.isLittleEndian === false && c.isExplicitVR === true
            );
            if (alt && alt.confidence >= best.confidence - 1) {
                best = alt;
            }
        }
        return best;
    }
}
